/**
 * Pure helpers for the CustomMap viewport-fitting (flyToBounds) logic.
 *
 * CustomMap only re-fits the map when the "thing it is focusing on" changes.
 * That thing is identified by a bounds key. On Farm View the user can switch
 * between farming units without selecting an individual field, so the key MUST
 * include the farming unit id — otherwise two units both showing "all fields"
 * (boundsTarget === '__all__') produce an identical key and the map never
 * re-fits to the newly selected unit (N-22).
 *
 * These functions are extracted so the behavior can be unit tested without a
 * Leaflet/React render harness.
 */

// Sentinel boundsTarget used when no individual field is selected (fit the
// whole farming unit). Kept in sync with paintPolygons in CustomMap.jsx.
const ALL_FIELDS_BOUNDS_TARGET = '__all__';

/**
 * Build the bounds key that identifies the current viewport target.
 * @param {string|number|null|undefined} farmingUnitId - id of the farming unit the map is showing
 * @param {string} boundsTarget - '__all__' or a selected field/container id
 * @returns {string}
 */
const computeBoundsKey = (farmingUnitId, boundsTarget) =>
  `${farmingUnitId ?? ''}:${boundsTarget}`;

/**
 * Decide whether the map should fly/fit to the given bounds.
 * Only fits when the bounds are valid AND the target changed since the last fit.
 * @param {boolean} isBoundsValid - result of L.LatLngBounds.isValid()
 * @param {string} boundsKey - key for the current target (from computeBoundsKey)
 * @param {string|null} lastFlownBoundsTarget - key of the last target we fit to
 * @returns {boolean}
 */
const shouldFlyToBounds = (isBoundsValid, boundsKey, lastFlownBoundsTarget) =>
  !!isBoundsValid && boundsKey !== lastFlownBoundsTarget;

module.exports = {
  ALL_FIELDS_BOUNDS_TARGET,
  computeBoundsKey,
  shouldFlyToBounds,
};
